/**
 * 素材库选择对话框组件
 * 用于从素材库中选择图片添加到上传表单
 */

import type { ReactElement } from 'react'
import { useState, useCallback, useEffect, useMemo } from 'react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useMaterialSelection } from '@/hooks/useMaterialSelection'
import { listMaterialsByBatch } from '@/services/materialService'
import { MaterialLibraryBatchList } from './MaterialLibraryBatchList'
import { MaterialLibraryImageGrid } from './MaterialLibraryImageGrid'
import type { CrawlBatch } from '@/types/crawlBatch'

/**
 * MaterialLibraryDialog 组件属性
 */
export interface MaterialLibraryDialogProps {
  /** 对话框是否打开 */
  open: boolean
  /** 打开状态变化回调 */
  onOpenChange: (open: boolean) => void
  /** 确认选择回调 */
  onConfirm: (imageUrls: string[]) => void
  /** 最大可选数量（可选） */
  maxCount?: number
  /** 已存在的 URL 列表（用于排除） */
  existingUrls?: string[]
}

/**
 * 素材库选择对话框组件
 * @param props 组件属性
 * @returns 组件元素
 */
export function MaterialLibraryDialog({
  open,
  onOpenChange,
  onConfirm,
  maxCount,
  existingUrls = [],
}: MaterialLibraryDialogProps): ReactElement {
  // 当前选中的批次
  const [selectedBatch, setSelectedBatch] = useState<CrawlBatch | null>(null)
  const [isSelectingWholeBatch, setIsSelectingWholeBatch] = useState(false)

  // 使用多选状态管理 Hook
  const {
    selectedUrls,
    countByBatch,
    totalCount,
    toggleUrl,
    selectAllInBatch,
    deselectAllInBatch,
    clearAll,
    getSelectedArray,
  } = useMaterialSelection()

  // 计算实际可选数量（排除已存在的 URL）
  const effectiveMaxCount = maxCount ?? undefined
  const existingUrlSet = useMemo((): Set<string> => {
    return new Set(existingUrls)
  }, [existingUrls])

  // 重置状态的回调
  const resetState = useCallback((): void => {
    clearAll()
    setSelectedBatch(null)
    setIsSelectingWholeBatch(false)
  }, [clearAll])

  /**
   * 对话框打开时重置状态
   */
  useEffect(() => {
    if (open) {
       
      resetState()
    }
  }, [open, resetState])

  /**
   * 处理批次选择
   */
  const handleSelectBatch = useCallback((batch: CrawlBatch): void => {
    setSelectedBatch(batch)
  }, [])

  /**
   * 处理确认选择
   */
  const handleConfirm = useCallback((): void => {
    const selectedArray = getSelectedArray()
    // 过滤掉已存在的 URL
    const newUrls = selectedArray.filter((url) => !existingUrlSet.has(url))
    onConfirm(newUrls)
    onOpenChange(false)
  }, [getSelectedArray, existingUrlSet, onConfirm, onOpenChange])

  /**
   * 处理取消
   */
  const handleCancel = useCallback((): void => {
    onOpenChange(false)
  }, [onOpenChange])

  /**
   * 全选当前素材库（跨分页）
   * 按 maxCount 剩余额度、已存在 URL、已选 URL 进行过滤
   */
  const handleSelectWholeBatch = useCallback(
    async (): Promise<void> => {
      if (selectedBatch === null || isSelectingWholeBatch) {
        return
      }

      const remainingSlots = effectiveMaxCount === undefined
        ? Infinity
        : Math.max(0, effectiveMaxCount - totalCount)

      if (remainingSlots <= 0) {
        toast.warning('已达到可选上限，无法继续全选')
        return
      }

      setIsSelectingWholeBatch(true)

      try {
        const pageSize = 100
        let page = 1
        const urlsToSelect: string[] = []
        const selectedUrlSet = new Set(selectedUrls)

        for (;;) {
          const response = await listMaterialsByBatch(selectedBatch.id, {
            current: page,
            pageSize,
          })

          for (const item of response.list) {
            const imageUrl = item.imageUrl
            if (
              imageUrl === '' ||
              existingUrlSet.has(imageUrl) ||
              selectedUrlSet.has(imageUrl)
            ) {
              continue
            }

            urlsToSelect.push(imageUrl)
            selectedUrlSet.add(imageUrl)

            if (urlsToSelect.length >= remainingSlots) {
              break
            }
          }

          if (
            urlsToSelect.length >= remainingSlots ||
            response.list.length === 0 ||
            page * pageSize >= response.total
          ) {
            break
          }

          page += 1
        }

        if (urlsToSelect.length === 0) {
          toast.info('该素材库没有可新增的图片')
          return
        }

        selectAllInBatch(urlsToSelect, selectedBatch.id)
        toast.success(`已全选 ${String(urlsToSelect.length)} 张图片`)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : '全选素材库失败'
        toast.error(errorMessage)
      } finally {
        setIsSelectingWholeBatch(false)
      }
    },
    [
      selectedBatch,
      isSelectingWholeBatch,
      effectiveMaxCount,
      totalCount,
      selectedUrls,
      existingUrlSet,
      selectAllInBatch,
    ],
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-[640px] max-h-[85vh] w-[960px] max-w-[90vw] flex-col gap-0 p-0 sm:max-w-[90vw]"
        onPointerDownOutside={(e): void => {
          e.preventDefault()
        }}
      >
        {/* 标题栏 */}
        <DialogHeader className="flex-shrink-0 border-b px-6 py-4">
          <DialogTitle className="text-lg font-semibold">
            从素材库选择图片
          </DialogTitle>
        </DialogHeader>

        {/* 主体内容 */}
        <div className="flex flex-1 overflow-hidden">
          {/* 左侧：批次列表 */}
          <MaterialLibraryBatchList
            selectedBatchId={selectedBatch?.id ?? null}
            onSelectBatch={handleSelectBatch}
            countByBatch={countByBatch}
          />

          {/* 右侧：图片网格 */}
          <div className="flex-1 overflow-hidden">
            <MaterialLibraryImageGrid
              batch={selectedBatch}
              selectedUrls={selectedUrls}
              onToggleUrl={toggleUrl}
              onSelectAll={selectAllInBatch}
              onSelectWholeBatch={(): void => { void handleSelectWholeBatch() }}
              onDeselectAll={deselectAllInBatch}
              currentSelectedCount={totalCount}
              isSelectingWholeBatch={isSelectingWholeBatch}
              {...(effectiveMaxCount !== undefined && { maxCount: effectiveMaxCount })}
            />
          </div>
        </div>

        {/* 底部操作栏 */}
        <div className="flex flex-shrink-0 items-center justify-between border-t px-6 py-4">
          <div className="text-sm text-muted-foreground">
            已选择{' '}
            <span className="font-semibold text-primary">{totalCount}</span>{' '}
            张图片
            {effectiveMaxCount !== undefined && (
              <span className="ml-1">
                （最多可选 {effectiveMaxCount} 张）
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
            >
              取消
            </Button>
            <Button
              type="button"
              onClick={handleConfirm}
              disabled={totalCount === 0}
            >
              确认选择
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
